import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { CollectionInvoiceDocument } from '../model';
import { formatDollar, formatOptionalDate } from '../utils/format';
import { groupInvoicesByDepartment } from '../utils/groupInvoices';

export interface SelectedInvoice {
  id: number;
  apply_to: string;
  filename: string;
  amount: number;
}

interface Props {
  open: boolean;
  documents: CollectionInvoiceDocument[];
  initialSelectedIds: number[];
  onClose: () => void;
  onConfirm: (selected: SelectedInvoice[]) => void;
}

type PdfBackedInvoiceDocument = CollectionInvoiceDocument & {
  pdf: NonNullable<CollectionInvoiceDocument['pdf']>;
};

// Only invoices with a stored PDF can be emailed; everything else is filtered out.
const pdfBacked = (documents: CollectionInvoiceDocument[]): PdfBackedInvoiceDocument[] =>
  documents.filter((doc): doc is PdfBackedInvoiceDocument => Boolean(doc.has_pdf && doc.pdf));

const toSelectedInvoice = (doc: PdfBackedInvoiceDocument): SelectedInvoice => ({
  id: doc.pdf.id,
  apply_to: doc.apply_to,
  filename: doc.pdf.filename,
  amount: doc.amount,
});

const InvoicePickerModal: React.FC<Props> = ({ open, documents, initialSelectedIds, onClose, onConfirm }) => {
  const selectable = useMemo(() => pdfBacked(documents), [documents]);
  const groups = useMemo(() => groupInvoicesByDepartment(selectable), [selectable]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) setSelectedIds(new Set(initialSelectedIds));
  }, [open, initialSelectedIds]);

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleConfirm = () => {
    // Dedup by pdf id: a multi-department invoice appears under each of its groups.
    const byId = new Map<number, SelectedInvoice>();
    selectable.forEach((doc) => {
      if (selectedIds.has(doc.pdf.id)) byId.set(doc.pdf.id, toSelectedInvoice(doc));
    });
    onConfirm(Array.from(byId.values()));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Attach invoices - {selectedIds.size} selected</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent dividers>
        {selectable.length === 0 ? (
          <Box sx={{ p: 2, color: 'text.secondary' }}>No invoices with PDFs available to attach.</Box>
        ) : (
          groups.map((group) => (
            <Box key={group.label} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {group.label} - {group.count} - {formatDollar(group.total)}
              </Typography>
              {group.invoices.map((doc) => (
                <Box
                  key={doc.pdf.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Checkbox size="small" checked={selectedIds.has(doc.pdf.id)} onChange={() => toggle(doc.pdf.id)} />
                  <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ width: 120 }}>
                    #{doc.apply_to}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 110 }}>
                    {formatOptionalDate(doc.invoice_date)}
                  </Typography>
                  <Typography variant="body2" sx={{ width: 110, textAlign: 'right' }}>
                    {formatDollar(doc.amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1, wordBreak: 'break-all' }}>
                    {doc.pdf.filename}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Attach ({selectedIds.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePickerModal;
